import React, { createContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import Swal from 'sweetalert2';
import API_BASE_URL from '../config/api';
import { devLog } from '../utils/devLog';
const AuthContext = createContext();

const REFRESH_INTERVAL_MS = 29 * 60 * 1000;

// H8: Draft snapshot side-channel — editor pages register a function that
// returns { tempNo, page, data } on demand. Before logoutUser fires, we
// call every registered snapshotter and stash what they return in
// localStorage under a recovery key so the next login can offer to restore.
const _draftSnapshotters = new Set();

export const registerDraftSnapshotter = (fn) => {
  if (typeof fn === 'function') _draftSnapshotters.add(fn);
  return () => _draftSnapshotters.delete(fn);
};

const recoveryDraftKey = (tempNo, page) =>
  `recovery_draft_${tempNo || 'unknown'}_${page || 'main'}`;

const stashRecoveryDrafts = () => {
  try {
    for (const fn of _draftSnapshotters) {
      try {
        const snap = fn();
        if (!snap || !snap.data) continue;
        const key = recoveryDraftKey(snap.tempNo, snap.page);
        localStorage.setItem(key, JSON.stringify({
          savedAt: new Date().toISOString(),
          tempNo: snap.tempNo,
          page: snap.page,
          data: snap.data,
        }));
      } catch (_) { /* ignore a single snapshot failure */ }
    }
  } catch (_) { /* ignore */ }
};

export const getRecoveryDraft = (tempNo, page = 'main') => {
  try {
    const raw = localStorage.getItem(recoveryDraftKey(tempNo, page));
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
};

export const clearRecoveryDraft = (tempNo, page = 'main') => {
  try {
    localStorage.removeItem(recoveryDraftKey(tempNo, page));
  } catch (_) { /* ignore */ }
};


export const AuthProvider = ({ children }) => {
  const [authToken, setAuthToken] = useState(() => {
    try {
      const stored = localStorage.getItem('authToken');
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  });
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem('authToken');
      if (!stored) return null;
      const parsed = JSON.parse(stored);
      return parsed?.access ? jwtDecode(parsed.access) : null;
    } catch { return null; }
  });
  const [loading, setLoading] = useState(true); 
  const navigate = useNavigate();

  const logoutUser = useCallback(() => {
    // H8: Before clearing auth, ask every registered editor for a snapshot
    // of the template JSON it's editing, so we can offer to restore on re-login.
    stashRecoveryDrafts();
    setAuthToken(null);
    setUser(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('access_token'); // Clean up admin token
    // /template-shot is a documented PUBLIC, no-login QA/gallery route. A failed
    // background token refresh must NOT bounce it to /login, or the public route
    // can never render for an unauthenticated viewer (e.g. screenshot tooling).
    if (!/^\/template-shot/.test(window.location.pathname)) {
      navigate('/login');
    }
  }, [navigate]);

  const updateToken = useCallback(async () => {
    if (!authToken?.refresh) {
      logoutUser();
      return;
    }
    try {
      const response = await axios.post(`${API_BASE_URL}/api/token/refresh/`, {
        refresh: authToken.refresh,
      });

      if (response.status === 200) {
        const newAuthToken = {
            ...authToken,
            access: response.data.access,
            // Update refresh token if rotation is enabled
            refresh: response.data.refresh || authToken.refresh,
        };
        setAuthToken(newAuthToken);
        setUser(jwtDecode(newAuthToken.access));
        localStorage.setItem('authToken', JSON.stringify(newAuthToken));
        localStorage.setItem('access_token', newAuthToken.access); // For admin API calls
      } else {
        // Show user-friendly message before logout
        Swal.fire({
          title: 'Session Expired',
          text: 'Your session has expired. Please login again.',
          icon: 'warning',
          confirmButtonText: 'Login'
        }).then(() => {
          logoutUser();
        });
      }
    } catch (error) {
      devLog.error('Token refresh failed:', error);
      // Show user-friendly message before logout
      Swal.fire({
        title: 'Session Expired',
        text: 'Your session has expired. Please login again.',
        icon: 'warning',
        confirmButtonText: 'Login'
      }).then(() => {
        logoutUser();
      });
    }
  }, [authToken, logoutUser]);

  const loginUser = async (e) => {
    e.preventDefault();
    const loginData = {
      email: e.target.email.value,
      password: e.target.password.value,
    };

    try {
      const response = await axios.post(`${API_BASE_URL}/api/token/`, loginData);
      if (response.status === 200) {
        setAuthToken(response.data);
        setUser(jwtDecode(response.data.access));
        localStorage.setItem('authToken', JSON.stringify(response.data));
        localStorage.setItem('access_token', response.data.access); // For admin API calls
        // Edge case (Agent 2): honor ?redirect=<path> after a 401-driven kick
        // so the user resumes their workflow instead of landing on /homepage.
        const params = new URLSearchParams(window.location.search);
        const redirect = params.get('redirect');
        const safeRedirect =
          redirect && redirect.startsWith('/') && !redirect.startsWith('//')
            ? redirect
            : '/homepage';
        navigate(safeRedirect);
      }
    } catch (error) {
      devLog.error('Login failed:', error);
      // Map known backend messages to user-friendly copy; never leak SimpleJWT defaults.
      const status = error.response?.status;
      const rawDetail = error.response?.data?.detail || '';
      const errorCode = error.response?.data?.code || '';
      let errorMessage;
      if (errorCode === 'not_verified' || /not verified/i.test(rawDetail)) {
        // Distinct from a wrong password: the account exists but isn't active.
        // No self-serve verification email exists — don't send users hunting
        // for a link that was never sent; point them at support instead.
        errorMessage = "Your account isn't active yet. Please contact us at team@vasstr.com or +91 97043 87878 and we'll activate it right away.";
      } else if (status === 401 || /no active account/i.test(rawDetail)) {
        errorMessage = 'Invalid email or password. Please try again.';
      } else if (status === 429) {
        errorMessage = 'Too many login attempts. Please wait a minute and try again.';
      } else if (!error.response) {
        errorMessage = 'Cannot reach the server. Please check your connection.';
      } else {
        errorMessage = 'Something went wrong. Please try again.';
      }
      Swal.fire({
        title: 'Login failed',
        text: errorMessage,
        icon: 'warning',
        confirmButtonText: 'OK',
        confirmButtonColor: '#d25614',
        allowOutsideClick: true,
        allowEscapeKey: true,
      });
    }
  };

  useEffect(() => {
    let interval;

    if (authToken) {
      interval = setInterval(() => {
        updateToken();
      }, REFRESH_INTERVAL_MS);
    }

    if (loading) {
      setLoading(false);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [authToken, updateToken, loading]);

  // Edge case (Agent 2, Category D): Cross-tab logout sync. When tab A logs out,
  // tab B's localStorage 'authToken' key disappears via the 'storage' event.
  // Without this listener, tab B keeps showing protected screens until its next
  // 401 round-trip. With it, all tabs sign out together.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'authToken' && e.newValue === null) {
        setAuthToken(null);
        setUser(null);
        // Don't double-snapshot recovery drafts here (the originating tab
        // already did it); just push to /login if we're not already there.
        if (!/^\/(login|register|Register|forgot-password)/.test(window.location.pathname)) {
          navigate('/login');
        }
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [navigate]);

  useEffect(() => {
    const storedAuthToken = localStorage.getItem('authToken');
    if (storedAuthToken) {
      try {
        const parsedToken = JSON.parse(storedAuthToken);
        if (parsedToken && parsedToken.access && parsedToken.refresh) {
          const decodedUser = jwtDecode(parsedToken.access);
          if (decodedUser.exp * 1000 < Date.now()) {
            // Skip writing parsedToken here — we already initialized from
            // localStorage in useState. Writing again creates a new object
            // reference and triggers a cascade of re-renders / duplicate API calls.
            (async () => {
                try {
                    const response = await axios.post(`${API_BASE_URL}/api/token/refresh/`, {
                        refresh: parsedToken.refresh,
                    });
                    if (response.status === 200) {
                        const newAuthToken = { ...parsedToken, access: response.data.access };
                        setAuthToken(newAuthToken);
                        setUser(jwtDecode(newAuthToken.access));
                        localStorage.setItem('authToken', JSON.stringify(newAuthToken));
                        localStorage.setItem('access_token', newAuthToken.access); // For admin API calls
                    } else {
                        logoutUser(); // Refresh failed
                    }
                } catch (err) {
                    logoutUser(); // Refresh failed
                } finally {
                    setLoading(false);
                }
            })();
            return; // Skip setting user from expired token
          } else {
            // Token is still valid AND we already populated state from
            // localStorage in useState init — no need to call setState again.
            // Only ensure the legacy `access_token` key exists for admin calls.
            if (!localStorage.getItem('access_token')) {
              localStorage.setItem('access_token', parsedToken.access);
            }
          }
        } else {
          localStorage.removeItem('authToken');
        }
      } catch (error) {
        localStorage.removeItem('authToken');
      }
    }
    setLoading(false);
  }, [logoutUser]);

  // Memoize context value so consumers don't re-render on every parent render.
  // Without this, hooks like `fetchProducts = useCallback(..., [authToken?.access, ...])`
  // appeared stable but the surrounding component still re-rendered on every
  // AuthProvider render, causing duplicate API calls.
  const contextData = useMemo(() => ({
    user,
    authToken,
    authTokens: authToken, // Add this for compatibility with existing components
    loading,
    loginUser,
    logoutUser,
  }), [user, authToken, loading, logoutUser]);

  return <AuthContext.Provider value={contextData}>{children}</AuthContext.Provider>;
};

export default AuthContext;